/****************************************************
 * Module Name:  departmentGroupMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Avatar, Box, Button, Chip, Tooltip } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import AddDepartmentGroup from "./addDepartmentGroup";
import DepartmentGroupProxy from "../../utility/proxy/manager/manufacturingProxy/departmentGroupProxy";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";

export class DepartmentGroupMaster extends Component {
  constructor(props) {
    super(props);

    this.state = {
      departmentGroup: [],

      departmentGroupOptions: [],
      descrOptions: [],

      showAddDepartmentGroupModal: false,
      editOperation: false,
      snackbarVariant: "",
      snackbarMessage: "",
      showSnackbar: "",
      sourceData: {},
      columnNames: [],
      accesDenied: false,
      loadingButton: false,
      showDepartmentGroupLoadSpinner: true,
      local_Permission: Constants.USER_PERMISSION.filter(
        (iPermission) =>
          iPermission.permissionid ===
          Constants.PERMISSIONS.MANUFACTURING_MASTER_DEPARTMENTGROUP
      ),
    };

    this.checkPermission()
  }

  componentDidMount() {
    PageTitle("MANUFACTURING_MASTER_DEPARTMENTGROUP");
  }

  checkPermission = () => {
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      this.setState({
        accesDenied: true
      })
    } else {
      this.fetchDepartmentGroupData(false);
    }
  }

  fetchDepartmentGroupData = (bAnimateSpinner) => {
    bAnimateSpinner && this.setState({ showDepartmentGroupLoadSpinner: true });
    DepartmentGroupProxy.GetAllDepartmentGroupDetail((responseData) => {
      if (responseData) {
        const uniqueFilterValues = {
          deptOptions: Utility.getDistinctOptions(responseData, 'departmentgroupname'),
          descriptionOptions: Utility.getDistinctOptions(responseData, 'departmentgroupdesc'),
        }
        this.setState({
          departmentGroup: responseData,
          showDepartmentGroupLoadSpinner: false,
        })
        const filterMap = this.generateFilterMap(Array.isArray(responseData) ? responseData : [responseData]);
        this.prepareColumns(filterMap, uniqueFilterValues);
      } else {
        this.setState({
          showDepartmentGroupLoadSpinner: false,
        })
        this.setSnackbarState(true, Constants.APiFailErrorMessage, "error");
      }
    }
    );
  };

  generateFilterMap = (responseData) => {
    const filterMap = {};

    // Process department Group Heads
    const departmentGroupHeads = responseData.flatMap((dept) =>
      dept.departmentgroupheads.map((head) => ({
        id: head.departmentgroupheaduserid,
        username: head.departmentgroupheadusername,
      }))
    );
    filterMap.departmentgroupheads = [
      ...new Set(
        departmentGroupHeads.map((dg) => `${dg.id} | ${dg.username}`)
      ),
    ];

    return filterMap;
  };

  prepareColumns = (filterMap, uniqueFilterValues) => {
    let columnNames = [];
    const columnData = this.initialColumns(uniqueFilterValues);

    columnData.forEach(iCol => {
      if (iCol.field === "departmentgroupheads") {
        iCol.type = "singleSelect";
        iCol.valueOptions = filterMap ? filterMap.departmentgroupheads : [];
        iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'departmentgroupheaduserid', 'departmentgroupheadusername');
      }
      columnNames.push(iCol);
    });

    this.setState({
      columnNames: columnNames
    })
  }

  initialColumns = (uniqueFilterValues) => [
    {
      field: "departmentgroupid",
      headerName: "ID",
      headerAlign: "center",
      type: 'number',
      align: "center",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    {
      field: "departmentgroupname",
      headerName: "DEPARTMENT GROUP",
      headerAlign: "center",
      align: "center",
      flex: 1,
      type: "singleSelect",
      valueOptions: uniqueFilterValues ? uniqueFilterValues.deptOptions : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "maxdayspacketallowed",
      headerName: "MAX DAYS PACKET ALLOWED",
      headerAlign: "center",
      width: 210,
      type: 'number',
      align: "center",
      renderCell: renderCellExpand,
    },
    {
      field: "departmentgroupdesc",
      headerName: "DESCRIP",
      headerAlign: "center",
      align: "center",
      flex: 1,
      type: "singleSelect",
      valueOptions: uniqueFilterValues ? uniqueFilterValues.descriptionOptions : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "departmentgroupheads",
      headerName: "GROUP HEAD",
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ minHeight: "auto", mt: 1 }}>
          {params.value.map((val, ind) => (
            <Chip
              key={ind}
              avatar={<Avatar>{val.departmentgroupheaduserid}</Avatar>}
              label={val.departmentgroupheadusername}
              //  onClick={() => {
              //    this.setState({
              //      showAddDepartmentGroupModal: true,
              //      editOperation: false,
              //      sourceData: undefined,
              //    });
              //  }}
              sx={{ margin: "0px 10px 10px 0px" }}
            />
          ))}
        </Box>
      ),
      sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "departmentgroupheadusername")
    },
    {
      field: "edit",
      headerName: "EDIT",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box
            style={{ textAlign: "center", cursor: "pointer" }}
            onClick={() => {
              this.setState({
                showAddDepartmentGroupModal: true,
                editOperation: true,
                sourceData: params.row,
              });
            }}
          >
            {" "}
            <BorderColorIcon color="primary" />
          </Box>
        );
      },
    },
  ];

  hideAddDepartmentGroupModal = () => {
    this.setState({
      showAddDepartmentGroupModal: false,
    });
  };

  handleValidation = (departmentGroupData) => {
    //TODO do validation if needed here
    return true;
  };

  addDepartmentGroup = (departmentGroupData) => {
    this.setState({ loadingButton: true });
    if (this.state.editOperation) {
      DepartmentGroupProxy.UpdateDepartmentGroup(
        departmentGroupData,
        (response) => {
          if (response) {
            this.fetchDepartmentGroupData(true);
            this.setState({ loadingButton: false, showAddDepartmentGroupModal: false });
            this.setSnackbarState(true, "Department Group updated successfully", "success");
          } else {
            this.setSnackbarState(true, Constants.APiFailErrorMessage, "error");
            this.setState({ loadingButton: false });
          }
        }
      );
    } else {
      DepartmentGroupProxy.AddDepartmentGroup(
        departmentGroupData,
        (response) => {
          if (response) {
            this.fetchDepartmentGroupData(true);
            this.setState({ loadingButton: false, showAddDepartmentGroupModal: false });
            this.setSnackbarState(true, "Department Group added successfully", "success");
          } else {
            this.setState({ loadingButton: false });
            this.setSnackbarState(true, Constants.APiFailErrorMessage, "error");
          }
        }
      );
    }
  };

  setSnackbarState = (show, message, variant) => {
    this.setState({
      showSnackbar: show,
      snackbarMessage: message,
      snackbarVariant: variant,
    })
  }

  render() {
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      return <NoPermission />
    }
    const rows = this.state.departmentGroup;
    const columns = Utility.isUpdateAllowed(this.state.local_Permission)
      ? this.state.columnNames
      : this.state.columnNames.filter((col) => col.field !== "edit");
    return (
      <Box>
        <CommonSnackbar
          show={this.state.showSnackbar}
          message={this.state.snackbarMessage}
          variant={this.state.snackbarVariant}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => this.setState({ showSnackbar: false })}
          title={this.state.snackbarVariant}
        />
        <br />
        {this.state.showAddDepartmentGroupModal ? (
          <AddDepartmentGroup
            Show={true}
            handleClose={this.hideAddDepartmentGroupModal}
            handleSave={this.addDepartmentGroup}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            loading={this.state.loadingButton}
          />
        ) : (
          <Box />
        )}
        <Box px={1}>
          <Box
            sx={{
              backgroundColor: '#fff',
              color: '#000',
              width: "100%",
              position: "relative",
              "& .MuiTablePagination-selectLabel": {
                marginTop: "15px",
              },
              "& .MuiTablePagination-displayedRows": {
                marginTop: "15px",
              },
              "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                fontWeight: Constants.defaultFontWeight,
              },
              "& .css-cc8tf1": {
                fontWeight: Constants.defaultFontWeight,
              },
              height: "83vh",
            }}
          >
            <DataGridPro
              disableRowSelectionOnClick={true}
              getRowHeight={() => "auto"}
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  color: '#fff',
                  backgroundColor: 'grey',
                },
                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                  py: "4px",
                },
                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                  py: "12px",
                },
                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                  py: "18px",
                },
                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
              }}
              density="compact"
              loading={this.state.showDepartmentGroupLoadSpinner}
              slots={{ toolbar: GridToolbar }}
              rows={rows}
              columns={columns}
              getRowId={(row) => row.departmentgroupid}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    // Hide columns status and traderName, the other columns will remain visible
                    departmentgroupid: false,
                  },
                },
                pagination: {
                  paginationModel: Constants.pageSize,
                },
              }}
              pageSizeOptions={Constants.pageSizeOptions}
              pagination
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
            <Box style={{ position: "absolute", top: 5, right: "270px" }}>
              {this.state.showDepartmentGroupLoadSpinner ? (
                <CircularProgress size="1rem" />
              ) : (
                <Tooltip title="Reload List">
                  <RefreshOutlinedIcon
                    color="primary"
                    fontSize="medium"
                    onClick={() => this.fetchDepartmentGroupData(true)}
                    sx={{ cursor: "pointer" }}
                  />
                </Tooltip>
              )}
              {Utility.isAddAllowed(this.state.local_Permission) ? (
                <Tooltip title="Add Department Group">
                  <Button
                    color="info"
                    variant="outlined"
                    size="small"
                    sx={{ ml: "5px" }}
                    onClick={() =>
                      this.setState({
                        showAddDepartmentGroupModal: true,
                        editOperation: false,
                        sourceData: {},
                      })
                    }
                    startIcon={<AddBoxOutlinedIcon size="large" />}
                  >
                    Department Group
                  </Button>
                </Tooltip>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
}

export default DepartmentGroupMaster;
